import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { ClearedStyleInput } from "@modules/Components";
import { FULL_STATE_NAMES } from "@constants/payfields";
import { go } from "@utils/fuzzySearch";

const MainContainer = styled.div`
  position: relative;
`;

const InputContainer = styled.div`
  height: 72px;
  border-radius: 5px;
  background-color: #ffffff;
  position: relative;
  box-shadow: ${props =>
    props.errorState
      ? "0 20px 70px 0 rgba(31, 32, 33, 0.05)"
      : "0 20px 70px 0 rgba(31, 32, 33, 0.12)"};
  border: ${props => props.setBorder};
  z-index: 2;

  @media (max-width: 375px) {
    z-index: 0;
  }
`;

const Select = styled(ClearedStyleInput)`
  width: 100%;
  border: 0;
  height: 100%;
  font-size: 16px;
  color: ${props => props.setColor};
  padding-left: 30px;
  display: flex;
  align-items: center;
  cursor: text;
  border-radius: 5px;

  @media (max-width: 500px) {
    padding-left: 10px;
  }
  @media (max-width: 375px) {
    padding-left: 20px;
    font-size: 14px !important;
  }
`;

const Option = styled.div`
  color: rgba(31, 32, 33, 0.8);
  cursor: pointer;
  margin-bottom: 10px;
`;

const Absolutecontainer = styled.div`
  top: 6px;
  position: absolute;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 1px;
  left: 30px;
  text-transform: uppercase;

  @media (max-width: 530px) {
    font-size: 9px;
  }
  @media (max-width: 500px) {
    left: 10px;
  }
  @media (max-width: 465px) {
    font-size: 8px;
  }
  @media (max-width: 375px) {
    left: 20px;
  }
`;

const ErrorContainer = styled(Absolutecontainer)`
  color: #ee5050;
`;

const Title = styled(Absolutecontainer)`
  color: rgba(31, 32, 33, 0.5);
`;

const DropDownContainer = styled.div`
  position: absolute;
  top: 66px;
  background-color: #ffffff;
  width: 100%;
  border-radius: 0px 0px 10px 10px;
  padding-left: 30px;
  z-index: 1;
  padding-top: 18px;
  padding-bottom: 10px;
  box-shadow: 0 5px 20px 0 rgba(38, 38, 38, 0.05);
`;

const DropDownInnerContainer = styled.div`
  max-height: 100px;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    width: 0px;
  }
`;

const RenderStateDropdownNew = ({
  input,
  className,
  meta: { error, touched },
  valuesFromZip,
  dynamicClass
}) => {
  const [openDropdown, changeOpenDropdown] = useState(false);
  const [mouseOver, changeMouseOver] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [stateArray, changeStateArray] = useState(FULL_STATE_NAMES);

  useEffect(() => {
    if (valuesFromZip) {
      input.onChange(valuesFromZip);
    }
  }, [valuesFromZip]);

  const handleFocus = e => {
    input.onFocus(e);
    setIsFocused(true);
    changeOpenDropdown(true);
  };

  const handleBlur = e => {
    setIsFocused(false);
    if (!mouseOver) {
      input.onBlur();
      changeOpenDropdown(false);
    }
    const formatted = formatName(e.target.value, true);
    if (!formatted) {
      input.onChange("");
    }
  };

  const formatName = (name, blurred) => {
    const findFullName = FULL_STATE_NAMES.find(
      item => item.name.toLowerCase() === name.toLowerCase()
    );
    const findByAbr = FULL_STATE_NAMES.find(
      item => item.abbreviation.toLowerCase() === name.toLowerCase()
    );
    if (findFullName) {
      return findFullName.abbreviation;
    } else if (findByAbr) {
      return findByAbr.abbreviation;
    } else if (blurred) {
      return "";
    }

    return name;
  };

  const handleChange = e => {
    input.onChange(formatName(e.target.value));
    const fuzz = go(e.target.value, FULL_STATE_NAMES);
    const newArray = fuzz.map(item => item.obj);
    changeStateArray(newArray);
  };

  return (
    <MainContainer className={dynamicClass}>
      <InputContainer
        errorState={touched && error}
        className="state-dropdown"
        setBorder={
          touched && error
            ? "solid 1px rgba(238, 80, 80, 0.48)"
            : isFocused
            ? "solid 2px #1f2021"
            : ""
        }
      >
        <Select
          name={input.name}
          onChange={handleChange}
          className={className}
          setColor={input.value ? "#1f2021" : "rgba(31, 32, 33, 0.65)"}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={input.value}
          placeholder={"State"}
        />

        <ErrorContainer>
          {touched && error && <div>{error}</div>}
        </ErrorContainer>
        <Title>{!error && input.value && <div>State</div>}</Title>
      </InputContainer>
      {openDropdown}
      {openDropdown && (
        <DropDownContainer
          onMouseOver={() => changeMouseOver(true)}
          onMouseOut={() => changeMouseOver(false)}
        >
          <DropDownInnerContainer>
            {stateArray.map((item, key) => (
              <Option
                onClick={async () => {
                  await input.onChange(item.abbreviation);
                  changeOpenDropdown(false);
                  changeMouseOver(false);
                  input.onBlur();
                }}
                key={key}
              >
                {item.abbreviation}
              </Option>
            ))}
          </DropDownInnerContainer>
        </DropDownContainer>
      )}
    </MainContainer>
  );
};

export default RenderStateDropdownNew;
